"use client";

import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

import { getOrCreateHomeEntryAttribution } from "@/utils/attribution";
import { AbExperiment, getOrAssignExperimentVariant } from "@/utils/abTesting";
import { trackEvent } from "@/utils/analytics";

type UseAbExperimentTrackingMode = "exposure" | "page_view" | "none";

type UseAbExperimentOptions = {
  trackingMode?: UseAbExperimentTrackingMode;
};

function getTrackingSessionKey(experimentId: string, variant: string, path: string, trackingMode: UseAbExperimentTrackingMode) {
  return `sr_ab_tracking:${trackingMode}:${experimentId}:${variant}:${path}`;
}

export function useAbExperiment(experiment: AbExperiment, { trackingMode = "exposure" }: UseAbExperimentOptions = {}) {
  const path = usePathname();
  const [variant, setVariant] = useState(experiment.variants[0]?.key ?? "control");
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const assignedVariant = getOrAssignExperimentVariant(experiment);
    setVariant(assignedVariant);
    setIsReady(true);

    if (trackingMode === "none") return;

    const trackingSessionKey = getTrackingSessionKey(experiment.id, assignedVariant, path, trackingMode);
    const attributionContext = getOrCreateHomeEntryAttribution();

    if (sessionStorage.getItem(trackingSessionKey)) {
      const skipLogKey = `${trackingSessionKey}:skip_logged`;
      if (!sessionStorage.getItem(skipLogKey)) {
        sessionStorage.setItem(skipLogKey, "1");
        trackEvent("ab_skipped", path, {
          reason: "already_tracked",
          experiment_id: experiment.id,
          variant: assignedVariant,
          page_location: window.location.pathname,
          ...attributionContext,
        });
      }
      return;
    }

    sessionStorage.setItem(trackingSessionKey, "1");

    if (trackingMode === "page_view") {
      trackEvent("page_view", path, {
        experiment_id: experiment.id,
        variant: assignedVariant,
        page_location: window.location.pathname,
        ...attributionContext,
      });
      return;
    }

    trackEvent("ab_exposed", path, {
      experiment_id: experiment.id,
      variant: assignedVariant,
      page_location: window.location.pathname,
      ...attributionContext,
    });

    trackEvent(`ab_exposed_${assignedVariant}`, path, {
      experiment_id: experiment.id,
      variant: assignedVariant,
      page_location: window.location.pathname,
      ...attributionContext,
    });
  }, [experiment, path, trackingMode]);

  return { variant, isReady };
}
